import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    MdArrowBack,
    MdSave,
} from 'react-icons/md';
import Loader from '../components/common/Loader';
import PaginatedSelect from '../components/common/PaginatedSelect';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './AddPayment.css';
import './AddGenerateRequest.css'; // Reuse common form styles
import Swal from 'sweetalert2';
import axios from 'axios';
import {
    UPDATE_RECEIPT_API,
    GET_RECEIPT_BY_ID_API,
    GET_PO_LIST_PENDING_PARTIAL_COMPLETED_API,
    GET_PO_DETAILS_BY_PO_ID_API,
    GET_PO_SUMMARY_API,
    GET_ALL_PROJECT_API,
    GET_PROJECT_VENDORS_API

} from '../config/API';
import { showErrorToast } from '../utils/toastUtils';
import Select from 'react-select';

const EditPayment = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        transactionType: 'receipt',
        partnerType: 'vendor',
        partnerId: null,
        projectId: null,
        poId: [],   // now an array for multi-select
        amount: '',
        date: new Date(),
        particular: '',
        payment_remark: '',
        payment_mode: 'Cash'
    });

    const [poDetailsMap, setPoDetailsMap] = useState({}); // Cache for PO details
    const [checkedPoIds, setCheckedPoIds] = useState([]); // Array of poIds that are checked in the table
    const [poAmounts, setPoAmounts] = useState({}); // { poId: amount } for individual POs

    const [poSummary, setPoSummary] = useState({
        poValue: 0,
        totalPaid: 0,
        outstanding: 0
    });

    useEffect(() => {
        const fetchPaymentData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.get(GET_RECEIPT_BY_ID_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status && response.data.data) {
                    const data = response.data.data;
                    const paymentDate = data.date ? new Date(data.date) : new Date();

                    // Support both single poId (legacy) and ReceiptPos array
                    const poList = [];
                    const checkedIds = [];
                    const amounts = {};

                    if (data.ReceiptPos && Array.isArray(data.ReceiptPos)) {
                        data.ReceiptPos.forEach(rp => {
                            const po = rp.PurchaseOrder;
                            if (po) {
                                const poEntry = {
                                    value: po.id,
                                    label: `${po.poNumber || po.id} (${po.ProjectMaster?.projectName || po.projectName || 'No Project'})`
                                };
                                poList.push(poEntry);
                                checkedIds.push(po.id);
                                amounts[po.id] = rp.amount;
                            }
                        });
                    } else if (data.PurchaseOrder) {
                        poList.push({
                            value: data.poId,
                            label: `${data.PurchaseOrder.poNumber || data.PurchaseOrder.id} (${data.PurchaseOrder.ProjectMaster?.projectName || data.PurchaseOrder.projectName || 'No Project'})`
                        });
                        checkedIds.push(data.poId);
                        amounts[data.poId] = data.amount;
                    } else if (data.PurchaseOrders && Array.isArray(data.PurchaseOrders)) {
                        data.PurchaseOrders.forEach(po => {
                            poList.push({
                                value: po.id,
                                label: `${po.poNumber || po.id} (${po.ProjectMaster?.projectName || po.projectName || 'No Project'})`
                            });
                            checkedIds.push(po.id);
                        });
                    }

                    setFormData({
                        transactionType: 'receipt',
                        partnerType: 'vendor',
                        partnerId: data.Vendor ? {
                            value: data.Vendor.id,
                            label: data.Vendor.vendorName
                        } : data.PurchaseOrder?.RequestVendor?.Vendor ? {
                            value: data.PurchaseOrder.RequestVendor.Vendor.id,
                            label: data.PurchaseOrder.RequestVendor.Vendor.vendorName
                        } : null,
                        projectId: data.ProjectMaster ? {
                            value: data.ProjectMaster.id,
                            label: data.ProjectMaster.projectName
                        } : data.PurchaseOrder?.ProjectMaster ? {
                            value: data.PurchaseOrder.ProjectMaster.id,
                            label: data.PurchaseOrder.ProjectMaster.projectName
                        } : null,
                        poId: poList,
                        amount: data.totalAmount || data.amount || '',
                        date: paymentDate,
                        particular: data.remark || '',
                        payment_remark: data.payment_remark || '',
                        payment_mode: data.payment_mode || 'Cash'
                    });

                    setCheckedPoIds(checkedIds);
                    setPoAmounts(amounts);

                    if (checkedIds.length > 0) {
                        fetchPoSummaries(checkedIds);
                    }
                }
            } catch (error) {
                console.error('Error fetching payment details:', error);
                showErrorToast('Failed to load payment details');
            } finally {
                setLoading(false);
            }
        };

        fetchPaymentData();
    }, [id]);

    const [vendorOptions, setVendorOptions] = useState([]);

    const fetchProjectVendors = async () => {
        if (!formData.projectId?.value) {
            setVendorOptions([]);
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_PROJECT_VENDORS_API(formData.projectId.value), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                const { assignedVendors = [], unassignedVendors = [] } = response.data.data || {};

                const mapVendor = (v) => ({
                    value: v.id || v._id,
                    label: v.companyName ? `${v.vendorName} (${v.companyName})` : v.vendorName
                });

                const groupedOptions = [];
                if (assignedVendors.length > 0) {
                    groupedOptions.push({
                        label: 'Assigned Vendors',
                        options: assignedVendors.map(mapVendor)
                    });
                }
                if (unassignedVendors.length > 0) {
                    groupedOptions.push({
                        label: 'All Other Vendors',
                        options: unassignedVendors.map(mapVendor)
                    });
                }

                setVendorOptions(groupedOptions.length > 0 ? groupedOptions : []);
            }
        } catch (error) {
            console.error('Error fetching project vendors:', error);
            setVendorOptions([]);
        }
    };

    useEffect(() => {
        if (formData.projectId?.value && !loading) {
            fetchProjectVendors();
        }
    }, [formData.projectId?.value, loading]);

    const fetchPoSummaries = async (poIds) => {
        if (!poIds || poIds.length === 0) return;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_PO_SUMMARY_API(poIds.join(',')), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status && response.data.data) {
                const summaries = response.data.data;
                const newDetailsMap = { ...poDetailsMap };

                summaries.forEach(data => {
                    newDetailsMap[data.id] = {
                        poValue: parseFloat(data.poAmount || 0),
                        totalPaid: parseFloat(data.paidAmount || 0),
                        outstanding: parseFloat(data.outstandingAmount || 0),
                        poNumber: data.poNumber || `PO #${data.id}`
                    };
                });

                setPoDetailsMap(newDetailsMap);
            }
        } catch (error) {
            console.error(`Error fetching PO summaries for IDs ${poIds}:`, error);
        }
    };

    // Recalculate summary whenever poDetailsMap or selected poId changes
    useEffect(() => {
        const selectedIds = formData.poId?.map(po => po.value) || [];
        let aggregatePoValue = 0;
        let aggregateTotalPaid = 0;

        selectedIds.forEach(id => {
            if (checkedPoIds.includes(id)) {
                const details = poDetailsMap[id];
                if (details) {
                    aggregatePoValue += details.poValue;
                    aggregateTotalPaid += details.totalPaid;
                }
            }
        });

        setPoSummary({
            poValue: aggregatePoValue,
            totalPaid: aggregateTotalPaid,
            outstanding: aggregatePoValue - aggregateTotalPaid
        });
    }, [poDetailsMap, formData.poId, checkedPoIds]);

    // Sync total amount from individual PO amounts
    useEffect(() => {
        const totalCheckedAmount = checkedPoIds.reduce((sum, id) => {
            return sum + parseFloat(poAmounts[id] || 0);
        }, 0);

        if (parseFloat(formData.amount || 0) !== totalCheckedAmount) {
            setFormData(prev => ({
                ...prev,
                amount: totalCheckedAmount > 0 ? totalCheckedAmount.toString() : ''
            }));
        }
    }, [poAmounts, checkedPoIds]);

    const handleSelectChange = (selected, field) => {
        if (field === 'projectId') {
            setFormData(prev => ({
                ...prev,
                projectId: selected,
                poId: []
            }));
            setPoDetailsMap({});
            setPoSummary({ poValue: 0, totalPaid: 0, outstanding: 0 });
            setCheckedPoIds([]);
            setPoAmounts({});
        } else if (field === 'partnerId') {
            setFormData(prev => ({
                ...prev,
                partnerId: selected,
                poId: []
            }));
            setPoDetailsMap({});
            setPoSummary({ poValue: 0, totalPaid: 0, outstanding: 0 });
            setCheckedPoIds([]);
            setPoAmounts({});
        } else if (field === 'poId') {
            const newSelection = selected || [];
            const previousIds = formData.poId?.map(po => po.value) || [];
            const currentIds = newSelection.map(po => po.value);

            const addedIds = currentIds.filter(id => !previousIds.includes(id));

            // Remove unchecked status for POs that are no longer selected
            setCheckedPoIds(prev => prev.filter(id => currentIds.includes(id)));
            // Remove amounts for POs that are no longer selected
            setPoAmounts(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(id => {
                    if (!currentIds.includes(id)) delete next[id];
                });
                return next;
            });

            // Auto-populate Vendor if not selected
            if (newSelection.length > 0 && !formData.partnerId) {
                const poData = newSelection[0].allData;
                const v = poData?.Vendor || poData?.VendorMaster;
                if (v) {
                    setFormData(prev => ({
                        ...prev,
                        partnerId: {
                            value: v.id || v._id,
                            label: v.companyName ? `${v.vendorName} (${v.companyName})` : v.vendorName,
                            whatsappNumber: v.whatsAppNumber || v.whatsappNumber || v.whatsapp_number || v.contactNumber || v.mobileNumber || ''
                        },
                        poId: newSelection
                    }));
                } else {
                    setFormData(prev => ({ ...prev, poId: newSelection }));
                }
            } else {
                setFormData(prev => ({
                    ...prev,
                    poId: newSelection
                }));
            }

            if (addedIds.length > 0) {
                fetchPoSummaries(addedIds);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: selected
            }));
        }
    };

    const handlePoAmountChange = (poId, value) => {
        setPoAmounts(prev => ({
            ...prev,
            [poId]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.partnerId || !formData.amount) {
            showErrorToast('Please fill all required fields');
            return;
        }

        if (formData.payment_mode === 'Cheque' && !formData.payment_remark?.trim()) {
            showErrorToast('Please enter cheque number');
            return;
        }

        const result = await Swal.fire({
            title: 'Update Transaction?',
            text: `Confirming updates to this ${formData.transactionType}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setSaving(true);
                const token = localStorage.getItem('accessToken');
                const payload = {
                    vendorId: formData.partnerId?.value,
                    projectId: formData.projectId?.value,
                    date: formData.date.toISOString().split('T')[0],
                    remark: formData.particular,
                    payment_remark: formData.payment_mode === 'Cheque' ? formData.payment_remark : '',
                    payment_mode: formData.payment_mode,
                    pos: checkedPoIds.map(id => ({
                        poid: id,
                        amount: parseFloat(poAmounts[id] || 0)
                    }))
                };

                const response = await axios.put(UPDATE_RECEIPT_API(id), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: 'Transaction updated successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/payment-management');
                } else {
                    showErrorToast(response.data.message || 'Failed to update transaction');
                }
            } catch (error) {
                console.error('Error updating payment:', error);
                showErrorToast('An error occurred while saving');
            } finally {
                setSaving(false);
            }
        }
    };



    return (
        <div className="add-po-page add-payment-page">
            <div className="page-header">
                <button onClick={() => navigate('/payment-management')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>Edit Payment</h2>
            </div>

            <form className="project-form payment-form" onSubmit={handleSubmit}>
                <div className="form-section">
                    <h3>Transaction Details</h3>



                    <div className="form-grid">
                        <div className="form-group">
                            <label>Date <span className="required-star">*</span></label>
                            <DatePicker
                                selected={formData.date}
                                onChange={(date) => setFormData(prev => ({ ...prev, date }))}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                portalId="root"
                                popperProps={{
                                    positionFixed: true
                                }}
                            />
                        </div>

                        <div className="form-group">
                            <label>Site / Project<span className="required-star">*</span></label>
                            <PaginatedSelect
                                apiUrl={GET_ALL_PROJECT_API}
                                mapResponse={(p) => ({
                                    value: p.id || p._id,
                                    label: p.projectName
                                })}
                                value={formData.projectId}
                                onChange={(sel) => handleSelectChange(sel, 'projectId')}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Select Site"
                                isClearable
                            />
                        </div>

                        <div className="form-group">
                            <label>Vendor Name <span className="required-star">*</span></label>
                            <Select
                                options={vendorOptions}
                                value={formData.partnerId}
                                onChange={(val) => handleSelectChange(val, 'partnerId')}
                                placeholder={formData.projectId?.value ? "Select Vendor" : "First select a project"}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable
                                isDisabled={!formData.projectId}
                                key={`vendors-${formData.projectId?.value || 'no-project'}`}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>

                        <div className="form-group">
                            <label>Purchase Order (PO)<span className="required-star">*</span></label>
                            <PaginatedSelect
                                isMulti
                                apiUrl={GET_PO_LIST_PENDING_PARTIAL_COMPLETED_API}
                                params={{
                                    vendorId: formData.partnerId?.value,
                                    projectId: formData.projectId?.value
                                }}
                                mapResponse={(p) => ({
                                    value: p.id || p._id,
                                    label: `${p.poNumber || p.id} (${p.ProjectMaster?.projectName || p.projectName || 'No Project'})`,
                                    allData: p
                                })}
                                value={formData.poId}
                                onChange={(sel) => handleSelectChange(sel, 'poId')}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Select one or more POs"
                                isClearable
                                isDisabled={false}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                key={`po-select-${formData.projectId?.value || 'none'}-${formData.partnerId?.value || 'none'}`}
                            />
                        </div>

                        {formData.poId?.length > 0 && (
                            <div className="form-group full-width" style={{ marginTop: '1rem' }}>
                                <div className="po-selection-table-container">
                                    <table className="items-table po-summary-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50px', textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={checkedPoIds.length === formData.poId.length && formData.poId.length > 0}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                const allIds = formData.poId.map(po => po.value);
                                                                setCheckedPoIds(allIds);
                                                                setPoAmounts(prev => {
                                                                    const next = { ...prev };
                                                                    formData.poId.forEach(po => {
                                                                        const details = poDetailsMap[po.value];
                                                                        const outstanding = details ? (details.poValue - details.totalPaid) : 0;
                                                                        if (!next[po.value]) {
                                                                            next[po.value] = outstanding > 0 ? outstanding.toString() : '0';
                                                                        }
                                                                    });
                                                                    return next;
                                                                });
                                                            } else {
                                                                setCheckedPoIds([]);
                                                                setPoAmounts({});
                                                            }
                                                        }}
                                                    />
                                                </th>
                                                <th style={{ textAlign: 'left' }}>PO Number</th>
                                                <th className="text-right" style={{ width: '130px' }}>PO Amount</th>
                                                <th className="text-right" style={{ width: '110px' }}>Paid</th>
                                                <th className="text-right" style={{ width: '130px' }}>Outstanding</th>
                                                <th className="text-right" style={{ width: '180px' }}>Paying Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.poId.map((po) => {
                                                const details = poDetailsMap[po.value];
                                                const isChecked = checkedPoIds.includes(po.value);
                                                const outstanding = details ? (details.poValue - details.totalPaid) : 0;
                                                return (
                                                    <tr key={po.value} className={isChecked ? 'selected-row' : ''}>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setCheckedPoIds(prev => [...prev, po.value]);
                                                                        if (!poAmounts[po.value]) {
                                                                            setPoAmounts(prev => ({
                                                                                ...prev,
                                                                                [po.value]: outstanding > 0 ? outstanding.toString() : '0'
                                                                            }));
                                                                        }
                                                                    } else {
                                                                        setCheckedPoIds(prev => prev.filter(id => id !== po.value));
                                                                        setPoAmounts(prev => {
                                                                            const next = { ...prev };
                                                                            delete next[po.value];
                                                                            return next;
                                                                        });
                                                                    }
                                                                }}
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'left', fontWeight: '500' }}>{details?.poNumber || po.label.split(' ')[0]}</td>
                                                        <td className="text-right" style={{ fontWeight: '500' }}>₹{details?.poValue?.toLocaleString() || '...'}</td>
                                                        <td className="text-right">₹{details?.totalPaid?.toLocaleString() || '...'}</td>
                                                        <td className="text-right" style={{ color: '#dc2626', fontWeight: '700' }}>
                                                            ₹{outstanding.toLocaleString()}
                                                        </td>
                                                        <td className="text-right">
                                                            <div className="table-input-wrapper" style={{ margin: '0 0 0 auto' }}>
                                                                <span className="currency-symbol">₹</span>
                                                                <input
                                                                    type="number"
                                                                    value={poAmounts[po.value] || ''}
                                                                    onChange={(e) => handlePoAmountChange(po.value, e.target.value)}
                                                                    placeholder="0.00"
                                                                    className="table-amount-input"
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="summary-card-horizontal" style={{
                                    marginTop: '1.5rem',
                                    background: '#f8fafc',
                                    padding: '1.25rem',
                                    borderRadius: '12px',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '1.5rem'
                                }}>
                                    <div className="summary-section">
                                        <div className="summary-label" style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Selected POs</div>
                                        <div className="summary-value" style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>{checkedPoIds.length} / {formData.poId.length}</div>
                                    </div>
                                    <div className="summary-section">
                                        <div className="summary-label" style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Aggregate PO Value</div>
                                        <div className="summary-value" style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>₹{poSummary.poValue.toLocaleString()}</div>
                                    </div>
                                    <div className="summary-section">
                                        <div className="summary-label" style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Aggregate Paid</div>
                                        <div className="summary-value" style={{ fontSize: '1.1rem', fontWeight: '700', color: '#059669' }}>₹{poSummary.totalPaid.toLocaleString()}</div>
                                    </div>
                                    <div className="summary-section">
                                        <div className="summary-label" style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Net Outstanding</div>
                                        <div className="summary-value" style={{ fontSize: '1.25rem', fontWeight: '800', color: '#dc2626' }}>₹{poSummary.outstanding.toLocaleString()}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label>Amount <span className="required-star">*</span></label>
                            <input
                                type="number"
                                name="amount"
                                value={formData.amount}
                                placeholder="0.00"
                                readOnly
                                className="form-control readonly-input"
                            />
                        </div>

                        <div className="form-group">
                            <label>Payment Mode <span className="required-star">*</span></label>
                            <select
                                name="payment_mode"
                                value={formData.payment_mode}
                                onChange={(e) => setFormData(prev => ({ ...prev, payment_mode: e.target.value }))}
                                className="form-control"
                            >
                                <option value="Cash">Cash</option>
                                <option value="Cheque">Cheque</option>
                            </select>
                        </div>

                        {formData.payment_mode === 'Cheque' && (
                            <div className="form-group">
                                <label>Cheque Number <span className="required-star">*</span></label>
                                <input
                                    type="text"
                                    name="payment_remark"
                                    value={formData.payment_remark}
                                    onChange={(e) => setFormData(prev => ({ ...prev, payment_remark: e.target.value }))}
                                    placeholder="Enter cheque number"
                                    className="form-control"
                                    required
                                />
                            </div>
                        )}

                        <div className="form-group full-width">
                            <label>Particular / Notes</label>
                            <textarea
                                name="particular"
                                value={formData.particular}
                                onChange={(e) => setFormData(prev => ({ ...prev, particular: e.target.value }))}
                                placeholder="Enter transaction details..."
                                rows="3"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/payment-management')} className="cancel-btn">
                        Cancel
                    </button>
                    <button type="submit" className="save-btn" disabled={saving}>
                        <MdSave /> {saving ? 'Updating...' : 'Update Payment'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditPayment;
